import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FlowchartStaticEdgeData,
  FlowchartEdge,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

import {StaticFlowchartNodeData, FlowchartNode} from './processFlowchartConfigEditorNodeTypes';
import {createInteractiveNodeFromStaticData, FlowchartOptions} from './processFlowchartConfigEditorUtils';

interface FlowchartData {
  nodes: Array<FlowchartNode>;
  edges: Array<FlowchartEdge>;
}

interface BuildInteractiveFlowchartFromStaticDataRunnerArgs {
  staticNodesData: Array<StaticFlowchartNodeData>;
  staticEdgesData: Array<FlowchartStaticEdgeData>;
  options: FlowchartOptions;
}

class BuildInteractiveFlowchartFromStaticDataRunner {
  private readonly staticNodesData: Array<StaticFlowchartNodeData>;
  private readonly staticEdgesData: Array<FlowchartStaticEdgeData>;
  private readonly options: FlowchartOptions;
  private nodes: Array<FlowchartNode>;
  private edges: Array<FlowchartEdge>;

  constructor(args: BuildInteractiveFlowchartFromStaticDataRunnerArgs) {
    this.staticNodesData = args.staticNodesData;
    this.staticEdgesData = args.staticEdgesData;
    this.options = args.options;
    this.nodes = [];
    this.edges = [];
  }

  run(): FlowchartData {
    this.convertStaticDataToNodes();
    this.convertStaticDataToEdges();
    return {
      nodes: this.nodes,
      edges: this.edges,
    };
  }

  private convertStaticDataToNodes(): void {
    // Sort nodes so that the root node is first and the output node is last
    const rootNode = this.staticNodesData.find(
      (staticNodeData) => staticNodeData.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
    );
    const outputNode = this.staticNodesData.find(
      (staticNodeData) =>
        staticNodeData.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
    );

    // It is not expected that the flowchart be empty since we should always have at least a root node.
    // So we can remove this check once we have validated that there are no real cases of an empty model.
    if (this.staticNodesData.length === 0) {
      this.nodes = [];
      return;
    }

    if (!rootNode) {
      throw new Error('No root node found');
    }

    const nodesToExclude = [rootNode.id];
    if (outputNode) {
      nodesToExclude.push(outputNode.id);
    }

    const taskNodes = this.staticNodesData.filter(
      (staticNodeData) => !nodesToExclude.includes(staticNodeData.id)
    );

    const orderedNodes = [rootNode, ...taskNodes];
    if (outputNode) {
      orderedNodes.push(outputNode);
    }

    this.nodes = orderedNodes.map((staticNodeData) =>
      createInteractiveNodeFromStaticData(staticNodeData, {x: 0, y: 0}, this.options)
    );
  }

  private convertStaticDataToEdges(): void {
    this.edges = this.staticEdgesData as Array<FlowchartEdge>;
  }
}

export default function buildInteractiveFlowchartFromStaticData(
  args: BuildInteractiveFlowchartFromStaticDataRunnerArgs
): FlowchartData {
  const runner = new BuildInteractiveFlowchartFromStaticDataRunner(args);
  return runner.run();
}
